import FetchRecordingPolicy from '@Application/RecordingPolicy/Queries/FetchRecordingPolicy';
import fetchRecordingPolicy from '@Application/RecordingPolicy/fetchRecordingPolicy';
import readDeviceRetention from '@Application/RecordingPolicy/readDeviceRetention';
import useAuthenticatedUser from '@UI/Providers/AuthenticatedUserProvider/useAuthenticatedUser';
import useLocalInterviewPort from '@UI/Providers/PortsProvider/Hooks/LocalInterviews/useLocalInterviewPort';
import useOutboxPort from '@UI/Providers/PortsProvider/Hooks/OutboxRunner/useOutboxPort';
import useLocalInterviews from '@UI/Pages/Beneficiaries/Hooks/useLocalInterviews';
import useOutboxItems from '@UI/Providers/PortsProvider/Hooks/OutboxRunner/useOutboxItems';
import * as React from 'react';
import { useDispatch, useStoreQuery } from '@verclam/synapse';

/**
 * Renders nothing: it only drives the outbox runner's and the local interview store's lifecycle
 * from the session. The transfer relies on the current session's cookies, and the runner refuses to
 * move an item whose owner differs from the one it was started with — starting it with the wrong
 * owner would misattribute every transfer to that owner on the server. The local interview store is
 * started and stopped on the exact same session: this is the single place that cages both local
 * stores to the current owner, and opening a second one would risk desynchronizing that cage and
 * exposing another device user's interviews.
 *
 * No capture screen ever needs to know about the recording policy: this is the sole place that
 * reads it and feeds it to the engine, so no screen gains a loading state for it.
 *
 * The engine is started with the retention persisted from the last successful fetch — read
 * synchronously, so it is available offline on the very first render of a session. When a fresh
 * policy later differs from that value, it is applied through `setRetention`, never through a
 * second `start()`: `start()` calls `stop()`, which aborts any in-flight transfer and clears the
 * cache, and its hydration only discriminates concurrent calls by owner, so restarting for the same
 * owner would let two hydrations run over each other. A failed policy fetch is not a screen error:
 * the engine already runs on the persisted value, so nothing is rendered and nothing is thrown.
 *
 * **Reconciliation.** A local interview is removed only when its own outbox item is *observed*, in
 * the current queue snapshot, in a terminal state (`'confirmed'` or `'expired'`). The absence of a
 * matching item is never a deletion trigger: it is the normal state of an interview still being
 * captured (its item does not exist yet) just as much as it is the normal state of the very first
 * snapshot of a session, taken before the queue has hydrated. Deleting on absence would erase every
 * interview mid-transfer on each reload, and every interview mid-capture, long before either is
 * actually done. The only case absence used to cover — the user discarding an interview outright —
 * is handled at that call site, which already holds the local interview, never inferred here from a
 * snapshot.
 */
const OutboxRunnerHost: React.FC = () => {
	const outboxPort = useOutboxPort();
	const localInterviewPort = useLocalInterviewPort();
	const dispatch = useDispatch();
	const { user, sessionStatus } = useAuthenticatedUser();
	const startedRetentionMsRef = React.useRef<number | null>(null);
	const outboxItems = useOutboxItems();
	const localInterviews = useLocalInterviews();

	const { data: policy } = useStoreQuery({
		selector: (state) => state.recordingPolicyStore.policy,
		stopOn: (value) => value !== null,
		query: () => {
			if (sessionStatus !== 'authenticated' || user === null) {
				return Promise.resolve();
			}

			return dispatch(fetchRecordingPolicy(new FetchRecordingPolicy()));
		},
		deps: [user?.id ?? null],
	});

	React.useEffect(() => {
		if (sessionStatus !== 'authenticated' || user === null) {
			startedRetentionMsRef.current = null;
			outboxPort.stop();
			localInterviewPort.stop();
			return undefined;
		}

		const persistedRetentionMs = readDeviceRetention();
		startedRetentionMsRef.current = persistedRetentionMs;
		outboxPort.start({ ownerUserId: user.id, retentionMs: persistedRetentionMs });
		localInterviewPort.start({ ownerUserId: user.id });

		return () => {
			outboxPort.stop();
			localInterviewPort.stop();
		};
	}, [outboxPort, localInterviewPort, sessionStatus, user]);

	React.useEffect(() => {
		if (policy === null) {
			return;
		}

		if (policy.deviceRetentionMs !== startedRetentionMsRef.current) {
			outboxPort.setRetention(policy.deviceRetentionMs);
			startedRetentionMsRef.current = policy.deviceRetentionMs;
		}
	}, [policy, outboxPort]);

	React.useEffect(() => {
		for (const localInterview of localInterviews) {
			if (localInterview.outboxItemId === null) {
				continue;
			}

			const outboxItem = outboxItems.find((item) => item.id === localInterview.outboxItemId);
			if (outboxItem === undefined) {
				continue;
			}

			if (outboxItem.state === 'confirmed' || outboxItem.state === 'expired') {
				void localInterviewPort.remove(localInterview.id);
			}
		}
	}, [localInterviews, outboxItems, localInterviewPort]);

	return null;
};

export default OutboxRunnerHost;
